import { useEffect, useRef, useState, type RefObject } from "react";

/** Which end of a scrolling row still has something behind it. */
type Reach = "none" | "start" | "end" | "both";

const FADES: Record<Reach, string> = {
  none: "",
  start: "scroll-fade-start",
  end: "scroll-fade-end",
  both: "scroll-fade-both",
};

/**
 * Watches a horizontally scrolling element and reports which edge should fade.
 *
 * A row with a hidden scrollbar whose last item finishes flush with the screen looks
 * finished. Softening the edge it continues past is the quietest way to say it is not,
 * and doing it from a measurement rather than a guess means a row that fits shows
 * nothing at all — no arrows, no bar, no permanent gradient over content that ends
 * where it appears to.
 *
 * `contents` is whatever changes the row's length. It re-runs the measurement and
 * re-observes the children, so a species chosen on the step above can add a dozen
 * mutation chips without the row still claiming it fits.
 */
export function useScrollReach<T extends HTMLElement>(
  contents: unknown,
): { ref: RefObject<T | null>; fade: string } {
  const ref = useRef<T>(null);
  const [reach, setReach] = useState<Reach>("none");

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    function measure(): void {
      const row = ref.current;

      if (!row) {
        return;
      }

      // A pixel of slack: fractional layout widths mean a row that fits exactly can
      // still report an overflow of half a pixel, which would fade an edge that has
      // nothing past it.
      const overflow = row.scrollWidth - row.clientWidth;

      if (overflow <= 1) {
        setReach("none");
        return;
      }

      const atStart = row.scrollLeft <= 1;
      const atEnd = row.scrollLeft >= overflow - 1;

      setReach(atStart ? "end" : atEnd ? "start" : "both");
    }

    measure();

    element.addEventListener("scroll", measure, { passive: true });

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    for (const child of element.children) {
      observer.observe(child);
    }

    return () => {
      element.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [contents]);

  return { ref, fade: FADES[reach] };
}
